import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import { formatDate } from '../../../config/appConfig';
import { useDocuments, type Document } from '../../documents/useData';
import GenericListWidget from './GenericListWidget';

const DocumentsRecent: React.FC<{
  limit?: number;
  embedded?: boolean;
  settings?: Record<string, unknown>;
}> = ({ limit = 5, embedded, settings }) => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const effectiveLimit = typeof settings?.limit === 'number' ? settings.limit : limit;
  const { data, isLoading } = useDocuments(1, '', {
    limit: 50,
    view: 'inbox',
    sortKey: 'updatedAt',
    sortDirection: 'desc',
  });

  const items = useMemo(() => {
    const list = [...(data ?? [])];
    list.sort(
      (a, b) =>
        new Date(b.updatedAt || b.createdAt).getTime() -
        new Date(a.updatedAt || a.createdAt).getTime(),
    );
    return list;
  }, [data]);

  return (
    <GenericListWidget<Document>
      title="Documents · Recent"
      viewAllPath="/documents"
      data={items}
      isLoading={isLoading}
      limit={effectiveLimit}
      emptyMessage="No documents"
      getItemKey={(doc) => doc.id}
      embedded={embedded}
      renderItem={(doc) => (
        <div
          role="button"
          tabIndex={0}
          onClick={() => navigate(`/documents/${doc.id}`)}
          onKeyDown={(e) => e.key === 'Enter' && navigate(`/documents/${doc.id}`)}
          className="flex items-center justify-between gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/40 cursor-pointer"
        >
          <div className="min-w-0">
            <p className={`text-sm font-medium truncate ${colors.text}`}>{doc.title}</p>
            {(doc.invoiceNumber || doc.fileType?.name) && (
              <p className={`text-xs truncate ${colors.textMuted}`}>
                {doc.invoiceNumber ? `Inv ${doc.invoiceNumber}` : doc.fileType?.name}
              </p>
            )}
          </div>
          <span className={`text-xs whitespace-nowrap ${colors.textMuted}`}>
            {formatDate(doc.updatedAt || doc.createdAt)}
          </span>
        </div>
      )}
    />
  );
};

export default DocumentsRecent;
